/* eslint-disable @typescript-eslint/dot-notation */
import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation } from '@angular/core';
import {
    AbstractControl,
    FormControl,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { NumericCellEditorInlineComponent } from './numeric-cell-editor-inline/numeric-cell-editor-inline.component';
import { NumericCellEditorPopupComponent } from './numeric-cell-editor-popup/numeric-cell-editor-popup.component';

/**
 * Editor Custom AgGrid for Numeric Input.
 * Params:
 * - min: (Optional) it allows to specify the minimum admissible value
 * - max: (Optional) it allows to specify the maximum admissible value
 * - decimal: (Optional. Default value 0) it allows to specify the number of decimals
 * - editInline (Default true) it allows to specify the preference between online or popup editor version.
 *  NOTE: when in the AgGrid is enabled the fullRow editing this input param is ignored and the editor will be inline
 */
export interface INumericCellEditorParams {
    inlineEditor: boolean; // true di default;
    required?: boolean;
    max?: number;
    min?: number;
    decimal?: number;
    separatorInUse?: string;
}

const NUMERIC_PATTERN = '^([+\\-])?(\\d+)?([\\.,])?(\\d+)?$';
const NUMERIC_REGEX = new RegExp(NUMERIC_PATTERN);

const convertStringToNumericValue = (input: string, decimal: number | null): number | null => {
    const matches = input.match(NUMERIC_REGEX);
    if (!matches) return null;
    const fullNumber = parseFloat(matches[0].replace(',', '.'));
    if (!Number.isFinite(fullNumber)) return null;
    if (decimal !== null) return parseFloat(fullNumber.toFixed(decimal));
    return fullNumber;
};

const toViewValue = (v: unknown, sep: string): string | null => {
    if (v == null) return null;
    if (typeof v === 'number' && Number.isFinite(v)) {
        const s = String(v);
        return sep === ',' ? s.replace('.', ',') : s;
    }
    if (typeof v === 'string') return v;
    return String(v);
};

const stringMinValidator =
    (min: number, decimal: number | null): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null => {
        const raw = control.getRawValue();
        if (typeof raw !== 'string' || raw.trim() === '') return null;
        const numericValue = convertStringToNumericValue(raw, decimal);
        if (numericValue === null || !Number.isFinite(numericValue)) return { invalidNumber: true };
        if (numericValue >= min) return null;
        return { min: { min: min, actual: numericValue } };
    };

const stringMaxValidator =
    (max: number, decimal: number | null): ValidatorFn =>
    (control: AbstractControl): ValidationErrors | null => {
        const raw = control.getRawValue();
        if (typeof raw !== 'string' || raw.trim() === '') return null;
        const numericValue = convertStringToNumericValue(raw, decimal);
        if (numericValue === null || !Number.isFinite(numericValue)) return { invalidNumber: true };
        if (numericValue <= max) return null;
        return { max: { max: max, actual: numericValue } };
    };
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-numeric-cell-editor',
    templateUrl: './numeric-cell-editor.component.html',
    styleUrls: ['./numeric-cell-editor.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, NumericCellEditorPopupComponent, NumericCellEditorInlineComponent],
})
export class NumericCellEditorComponent implements ICellEditorAngularComp {
    readonly decimal = signal<number | null>(null);
    readonly min = signal<number | null>(null);
    readonly max = signal<number | null>(null);
    readonly editInline = signal<boolean>(false);
    readonly separatorInUse = signal<string>('');
    readonly cellWidth = signal<string>('');
    readonly cellReady = signal<boolean>(false);
    readonly formControl = new FormControl<string | null>(null);

    agInit(params: ICellEditorParams & INumericCellEditorParams): void {
        this.cellWidth.set(params.column.getActualWidth() + 'px');
        this.editInline.set(params.inlineEditor === false ? false : true);
        const min = params.min ?? null;
        const max = params.max ?? null;
        const decimal = params.decimal ?? null;
        this.decimal.set(decimal);
        this.min.set(min);
        this.max.set(max);
        this.separatorInUse.set(params.separatorInUse ?? '');
        const validators: ValidatorFn[] = [];
        if (params.required) validators.push(Validators.required);
        if (min != null) validators.push(stringMinValidator(min, decimal));
        if (max != null) validators.push(stringMaxValidator(max, decimal));
        this.formControl.setValidators(validators);
        const initial = toViewValue(params.value, params.separatorInUse ?? '');
        this.formControl.setValue(initial ?? null, { emitEvent: false });
        this.formControl.updateValueAndValidity({ emitEvent: false });
    }

    getValue(): number | null {
        const decimal = this.decimal();
        if (this.formControl && !this.formControl.invalid) {
            // Alla chiusura dell'editor se il campo è valido aggiorniamo il valore altrimenti no
            const raw = this.formControl.getRawValue();
            if (typeof raw !== 'string' || raw.trim() === '') return null;
            const s = raw.trim()
            return convertStringToNumericValue(s, decimal);
        }
        return null;
    }

    isCancelBeforeStart(): boolean {
        return false;
    }

    isPopup(): boolean {
        return !this.editInline();
    }

    afterGuiAttached(): void {
        this.cellReady.set(true);
    }
}
